import {
  Dialog,
  DialogPanel,
  DialogPanelProps,
  Transition,
  TransitionChild,
  TransitionEvents,
} from '@headlessui/react';
import { clstx, cvaMerge } from '@payaca/utilities/cvaMerge';
import { forwardRef, Fragment, PropsWithChildren } from 'react';

const sidebarContainerClass = cvaMerge(
  'fixed flex transform flex-col bg-white transition-all duration-300',
  {
    variants: {
      size: {
        sm: 'inset-y-0 h-full w-[min(280px,100vw)]',
        // `md:inset-x-auto` undoes the mobile bottom-sheet's full-width inset
        // so the side-specific `md:start-0` / `md:end-0` from compoundVariants
        // can anchor the drawer to just one edge on desktop.
        standard:
          'inset-x-0 top-12 h-[calc(100svh-var(--spacing-12))] rounded-t-xl border-t pb-[env(safe-area-inset-bottom)] md:inset-x-auto md:inset-y-0 md:top-0 md:h-full md:rounded-t-none md:border-t-0 md:pb-0 md:w-[min(500px,100vw)]',
        lg: 'inset-x-0 top-12 h-[calc(100svh-var(--spacing-12))] rounded-t-xl border-t pb-[env(safe-area-inset-bottom)] md:inset-x-auto md:inset-y-0 md:top-0 md:h-full md:rounded-t-none md:border-t-0 md:pb-0 md:w-[min(1000px,100vw)]',
      },
      side: { left: '', right: '' },
      zIndexLevel: { 1: 'z-sidebar1', 2: 'z-sidebar2' },
    },
    compoundVariants: [
      { size: 'sm', side: 'left', class: 'start-0 border-e' },
      { size: 'sm', side: 'right', class: 'end-0 border-s' },
      { size: 'standard', side: 'left', class: 'md:start-0 md:border-e' },
      { size: 'standard', side: 'right', class: 'md:end-0 md:border-s' },
      { size: 'lg', side: 'left', class: 'md:start-0 md:border-e' },
      { size: 'lg', side: 'right', class: 'md:end-0 md:border-s' },
    ],
  }
);

const sidebarPanelClass = cvaMerge('transition-transform duration-300', {
  variants: {
    size: { sm: '', standard: '', lg: '' },
    side: { left: '', right: '' },
    behind: { true: '', false: '' },
  },
  compoundVariants: [
    {
      behind: true,
      size: 'standard',
      side: 'left',
      class: 'md:translate-x-36',
    },
    {
      behind: true,
      size: 'standard',
      side: 'right',
      class: 'md:-translate-x-36',
    },
    { behind: true, size: 'lg', side: 'left', class: 'md:translate-x-52' },
    { behind: true, size: 'lg', side: 'right', class: 'md:-translate-x-52' },
  ],
});

export interface ISidebarCoreProps extends TransitionEvents {
  isOpen: boolean;
  onClose?: () => void;
  className?: string;
  disableBackdropClick?: boolean;
  panelProps?: DialogPanelProps;
  size?: 'sm' | 'standard' | 'lg';
  side?: 'left' | 'right';
  behind?: boolean;
  zIndexLevel?: 1 | 2;
}

const SidebarCore = forwardRef<
  HTMLDivElement,
  PropsWithChildren<ISidebarCoreProps>
>((props, ref) => {
  const {
    className,
    children,
    isOpen,
    onClose,
    disableBackdropClick,
    panelProps,
    size = 'standard',
    side = 'right',
    behind = false,
    zIndexLevel = 1,
    ...rest
  } = props;

  return (
    <Transition show={isOpen} appear as={Fragment} {...rest}>
      <Dialog onClose={disableBackdropClick ? () => null : () => onClose?.()}>
        {/*Backdrop*/}
        <TransitionChild
          enter="transition-opacity duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-300 delay-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          as={Fragment}
        >
          <div
            className={clstx(
              'del fixed inset-0 bg-gray-900/50 dark:bg-gray-900/80',
              zIndexLevel === 1 && 'z-sidebar-backdrop-1',
              zIndexLevel === 2 && 'z-sidebar-backdrop-2'
            )}
            aria-hidden="true"
          />
        </TransitionChild>

        {/*
          Standard / lg drawers slide up from the bottom on mobile (bottom
          sheet) and from the side on md+. Small drawers stay side-attached
          everywhere — they're narrow enough to read on a phone and the
          callers expect a slim drawer next to the page rather than a sheet
          covering it.
        */}
        <TransitionChild
          enter="transition duration-300"
          enterFrom={clstx(
            'transform opacity-0',
            size === 'sm'
              ? side === 'left'
                ? '-translate-x-full'
                : 'translate-x-full'
              : clstx(
                  'translate-y-full md:translate-y-0',
                  side === 'left'
                    ? 'md:-translate-x-full'
                    : 'md:translate-x-full'
                )
          )}
          enterTo="transform translate-x-0 translate-y-0 opacity-100"
          leave="transition duration-300"
          leaveFrom="transform translate-x-0 translate-y-0 opacity-100"
          leaveTo={clstx(
            'transform opacity-0',
            size === 'sm'
              ? side === 'left'
                ? '-translate-x-full'
                : 'translate-x-full'
              : clstx(
                  'translate-y-full md:translate-y-0',
                  side === 'left'
                    ? 'md:-translate-x-full'
                    : 'md:translate-x-full'
                )
          )}
          as={Fragment}
        >
          <div className={sidebarContainerClass({ size, side, zIndexLevel })}>
            <DialogPanel
              ref={ref}
              className={clstx(
                className,
                sidebarPanelClass({ size, side, behind })
              )}
              {...panelProps}
            >
              {children}
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
});

export default SidebarCore;
